import { inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  setEntity,
  updateEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { SelectedEntityState } from '@shared/store/selected-entity.feature';
import { toast } from 'ngx-sonner';
import { pipe, switchMap, tap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { GroupeÉtapesÀLaCarte } from '../../models/groupe-étapes-à-la-carte.model';
import { Étape } from '../../models/étape.model';
import { CreateGroupeÉtapesRequest } from '../../requests/create-groupe-étapes-request';
import { UpdateGroupeÉtapesRequest } from '../../requests/update-groupe-étapes-request';
import { GroupeÉtapesService } from '../../services/groupe-étapes.service';
import { HttpErrorResponse } from '@angular/common/http';

export function avecMéthodesGroupeÉtapes<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<GroupeÉtapesÀLaCarte> & SelectedEntityState>(),
      props: type<EntityProps<GroupeÉtapesÀLaCarte> & { selectedEntity: Signal<GroupeÉtapesÀLaCarte | null> }>(),
    },
    withMethods((store) => ({
      chargerGroupes: (state: { groupes: GroupeÉtapesÀLaCarte[] }) => patchState(store, setAllEntities(state.groupes)),
      ajouterGroupe: (state: { groupe: GroupeÉtapesÀLaCarte }) => patchState(store, setEntity(state.groupe)),
      modifierGroupe: (state: { id: string; groupe: Partial<GroupeÉtapesÀLaCarte> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.groupe })),
      supprimerGroupe: (state: { id: string }) => patchState(store, removeEntity(state.id)),
    })),
    withMethods((store, service = inject(GroupeÉtapesService)) => ({
      selection: rxMethod<string>(
        pipe(
          switchMap((idGroupe) => service.findById(idGroupe)),
          tapResponse({
            next: (groupe) => {
              store.ajouterGroupe({ groupe });
            },
            error: (err) => console.error(err),
          }),
        ),
      ),
      créer: rxMethod<CreateGroupeÉtapesRequest>(
        pipe(
          switchMap((request) =>
            service.créer(request).pipe(
              tapResponse({
                next: (groupe) => store.ajouterGroupe({ groupe }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création du groupe, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idGroupe: string;
        data: UpdateGroupeÉtapesRequest;
        next?: () => void;
        finalize?: () => void;
      }>(
        pipe(
          tap(({ idGroupe, data }) => store.modifierGroupe({ id: idGroupe, groupe: data })),
          switchMap(({ idGroupe, data, next, finalize }) =>
            service.modifier(idGroupe, data).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification du groupe d'étapes à la carte, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
                finalize: () => finalize?.(),
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ id: string; next: () => void }>(
        pipe(
          tap(({ id }) => store.supprimerGroupe({ id })),
          switchMap(({ id, next }) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de l'étape, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      toggleStep: (étape: Étape, idGroupe: string) => {
        const groupeÉtapes = store.entityMap()[idGroupe];
        if (!groupeÉtapes) throw Error();
        const index = groupeÉtapes.etapes.findIndex((x) => x.id === étape.id);
        if (index !== -1) {
          groupeÉtapes.etapes.splice(index, 1);
        } else {
          groupeÉtapes.etapes.push(étape);
        }
        return store.modifierGroupe({ id: groupeÉtapes.id, groupe: groupeÉtapes });
      },
    })),
  );
}
